const StateType = {
    INITIATE: "initiate",
    STARTED: "started",
    STOPPED: "stopped"
}
Object.freeze(StateType);

class WebSpeechRecognition {
    #onError = console.error;
    #onTranscript = console.log;
    #onInterimResult = console.log;
    #state = StateType.INITIATE;
    #options = { continuous: true, interimResults: false, lang: "en-IN" };

    #transcript = '';
    #speechRecognition = null;
    #speechEnd = false;

    /**
     * @param {function} onTranscript - Callback to get final transcript
     * @param {function} onInterimResult - Callback to get interim transcript
     * @param {function} onError - Callback to handle error
     * @param {object} options - Options for WebSpeechRecongition config
     */
    constructor(onTranscript, onInterimResult, onError, options = {}) {
        console.log("DEBUG::init with options", options);
        this.loadSpeechRecognition();

        if (onTranscript) this.#onTranscript = onTranscript;
        if (onInterimResult) this.#onInterimResult = onInterimResult;
        if (onError) this.#onError = onError;

        if (options?.continuous) this.#options.continuous = options.continuous;
        if (options?.lang) this.#options.lang = options.lang;
        if (options?.interimResults) this.#options.interimResults = options.interimResults;
    }

    loadSpeechRecognition = () => {
        console.log("DEBUG::loadSpeechRecognition");

        if (('webkitSpeechRecognition' in window)) {
            this.#speechRecognition = new window.webkitSpeechRecognition() ||
                new window.SpeechRecognition();;

            this.#speechRecognition.lang = this.#options.lang;
            this.#speechRecognition.continuous = this.#options.continuous;
            this.#speechRecognition.interimResults = this.#options.interimResults;

            this.#speechRecognition.onerror = this.#handleOnError;
            this.#speechRecognition.onend = this.#handleOnEnd;
            this.#speechRecognition.onresult = this.#handleOnResult;
            this.#speechRecognition.onstop = () => console.log("DEBUG::onstop");
            this.#speechRecognition.onstart = () => console.log("DEBUG::onstart");
            return true;
        }

        this.#onError(new Error("Unsupported browser."));
        return false;
    };

    start = () => {
        console.log("DEBUG::start");

        if (!this.#speechRecognition) {
            const result = this.loadSpeechRecognition();

            if (!result) {
                this.#onError(new Error("Unable to initialize speech recognition"));
                return;
            }
        }

        if (this.#state === StateType.INITIATE ||
            this.#state === StateType.STOPPED) {
            this.#transcript = '';
            this.#speechEnd = false;
            this.#speechRecognition?.start();
            this.#state = StateType.STARTED;
        }
    };

    stop = () => {
        console.log("DEBUG::stop");
        if (this.#state === StateType.STARTED) {
            this.#state = StateType.STOPPED;
            this.#speechRecognition?.stop();

            if (this.#speechEnd) {
                this.#onTranscript(this.#transcript);
            }
        }
    };

    #handleOnResult = (event) => {
        console.log("DEBUG::handleOnResult");
        for (let i = event.resultIndex; i < event.results.length; ++i) {
            if (event.results[i].isFinal) {
                this.#transcript += event.results[i][0].transcript;
            }
        }
        this.#onInterimResult(this.#transcript);
    };

    #handleOnEnd = () => {
        console.log("DEBUG::handleOnEnd");
        if (this.#state === StateType.STARTED) {
            this.#speechRecognition?.start();
        }

        this.#speechEnd = true;
        console.log("DEBUG::SpeechEnded: ", this.#transcript);
        if (this.#state === StateType.STOPPED) {
            this.#onTranscript(this.#transcript);
        }
    };

    #handleOnError = (event) => {
        console.log("DEBUG::handleOnError", event);
    };
}

module.exports = WebSpeechRecognition;